import { useState, useCallback } from "react";
import { Button } from "@/components/ui/button";
import { MapPin, Loader2, Check } from "lucide-react";
import { useGeolocation } from "@/hooks/useGeolocation";
import { toast } from "@/hooks/use-toast";

interface LocationButtonProps {
  hasLocation: boolean;
  onLocationUpdated?: () => void;
}

const LocationButton = ({ hasLocation, onLocationUpdated }: LocationButtonProps) => {
  const { loading, requestLocation } = useGeolocation();
  const [showSuccess, setShowSuccess] = useState(false);

  const handleClick = useCallback(async () => {
    const result = await requestLocation();
    
    if (result.success) {
      setShowSuccess(true);
      toast({
        title: "📍 Location Updated",
        description: "We'll show you people nearby!"
      });
      onLocationUpdated?.();
      
      setTimeout(() => setShowSuccess(false), 2000);
    } else {
      toast({
        title: "Location Error",
        description: result.error || "Unable to get your location",
        variant: "destructive"
      });
    }
  }, [requestLocation, onLocationUpdated]);

  if (showSuccess) {
    return (
      <Button variant="outline" disabled className="gap-2 w-full">
        <Check className="w-4 h-4 text-primary" />
        Location Updated
      </Button>
    );
  }

  if (loading) {
    return (
      <Button variant="outline" disabled className="gap-2 w-full">
        <Loader2 className="w-4 h-4 animate-spin" />
        Getting Location...
      </Button>
    );
  }

  return (
    <Button
      variant={hasLocation ? "outline" : "default"}
      onClick={handleClick}
      className="gap-2 w-full"
    >
      <MapPin className="w-4 h-4" />
      {hasLocation ? "Update Location" : "Enable Location"}
    </Button>
  );
};

export default LocationButton;